import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios, { type AxiosError } from 'axios';

import { invalidateRecordCachesExceptSubject } from './recordCacheInvalidation';

type PreviewData = Diagonal.Data.Data.RecordDeletionPreviewData;

/**
 * Shape of the JSON body returned by the API when a record delete is blocked
 * after the preview was shown. This hook always sends the confirmation header,
 * so the expected structured non-2xx response here is HTTP 409.
 */
export type DeleteResourceRecordErrorBody = PreviewData & {
  title: string;
  status: 409;
  detail: string;
};

/**
 * Header callers send to opt in to the secondary effects (clears/archives)
 * of a record delete. Only the literal string `'true'` opts in; the backend
 * ignores any other value.
 */
export const DELETE_CONFIRMATION_HEADER = 'X-Diagonal-Delete-Confirmed';
export const DELETE_CONFIRMATION_HEADER_VALUE = 'true';

interface UseDeleteResourceRecordOptions {
  resourceId: string | undefined;
  recordId: string | undefined;
  /**
   * Whether to fetch the delete preview now. The block opens this gate when
   * the confirmation dialog is opened so the preview matches the moment of
   * decision rather than first render.
   */
  fetchPreview?: boolean;
  onSuccess?: () => void;
}

export interface DeleteResourceRecordState {
  preview: PreviewData | undefined;
  isPreviewLoading: boolean;
  isPreviewError: boolean;
  confirm: () => void;
  isProcessing: boolean;
  /**
   * Structured error body returned by the API on a blocked delete response.
   * `null` for successful deletes and generic transport errors.
   */
  deleteError: DeleteResourceRecordErrorBody | null;
  reset: () => void;
}

function previewUrlFor(resourceId: string | undefined, recordId: string | undefined): string {
  return `/api/data/resources/${encodeURIComponent(resourceId ?? '')}/records/${encodeURIComponent(recordId ?? '')}/delete-preview`;
}

function deleteUrlFor(resourceId: string | undefined, recordId: string | undefined): string {
  return `/api/data/resources/${encodeURIComponent(resourceId ?? '')}/records/${encodeURIComponent(recordId ?? '')}`;
}

export function useDeleteResourceRecord({
  resourceId,
  recordId,
  fetchPreview = false,
  onSuccess
}: UseDeleteResourceRecordOptions): DeleteResourceRecordState {
  const queryClient = useQueryClient();

  const previewQuery = useQuery<PreviewData>({
    enabled: fetchPreview && Boolean(resourceId && recordId),
    queryKey: ['record-delete-preview', resourceId, recordId],
    queryFn: () => axios.get(previewUrlFor(resourceId, recordId)).then((response) => response.data)
  });

  const deleteMutation = useMutation<unknown, AxiosError<DeleteResourceRecordErrorBody>>({
    mutationFn: () =>
      axios.delete(deleteUrlFor(resourceId, recordId), {
        headers: {
          [DELETE_CONFIRMATION_HEADER]: DELETE_CONFIRMATION_HEADER_VALUE
        }
      }),
    onSuccess: () => {
      void invalidateRecordCachesExceptSubject(queryClient, resourceId, recordId);
      onSuccess?.();
    }
  });

  const deleteError = (() => {
    const error = deleteMutation.error;
    if (!error || !axios.isAxiosError<DeleteResourceRecordErrorBody>(error)) {
      return null;
    }
    const body = error.response?.data;
    if (
      error.response?.status !== 409 ||
      !body ||
      typeof body !== 'object' ||
      body.outcome === null ||
      typeof body.outcome !== 'object'
    ) {
      return null;
    }
    return body;
  })();

  return {
    preview: previewQuery.data,
    isPreviewLoading: previewQuery.isLoading,
    isPreviewError: previewQuery.isError,
    confirm: () => {
      if (!resourceId || !recordId) {
        return;
      }
      deleteMutation.mutate();
    },
    isProcessing: deleteMutation.isPending,
    deleteError,
    reset: () => {
      deleteMutation.reset();
    }
  };
}
